import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { User as SelectUser } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient } from "../lib/queryClient";
import { getUserMessage } from "../lib/errorMessages";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

// Define the registration data type
type RegistrationData = {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phone?: string;
};

// Define the login data type
type LoginData = {
  email: string;
  password: string;
};

type AuthContextType = {
  user: SelectUser | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<boolean, Error, void>;
  registerMutation: UseMutationResult<SelectUser, Error, RegistrationData>;
  deleteAccountMutation: UseMutationResult<boolean, Error, void>;
  requestMagicLinkMutation: UseMutationResult<any, Error, string>;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [authChecked, setAuthChecked] = useState(false);
  const [, navigate] = useLocation();

  // Check for active session flag in localStorage
  useEffect(() => {
    const hasActiveSession =
      localStorage.getItem("auth_session_active") === "true";
    console.log(
      "Auth provider initialized, active session flag:",
      hasActiveSession
    );
    setAuthChecked(true);
  }, []);

  // If a magic link token is present in the URL, verify it on first load
  useEffect(() => {
    if (!authChecked) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");
      if (token) {
        // Verify and then clean the URL so the token isn't persisted
        (async () => {
          try {
            await verifyMagicLink(token);
          } finally {
            const url = new URL(window.location.href);
            url.searchParams.delete("token");
            window.history.replaceState({}, "", url.toString());
          }
        })();
      }
    } catch (e) {
      // no-op
    }
  }, [authChecked]);

  const {
    data: user,
    error,
    isLoading,
    refetch: refetchUser,
  } = useQuery<SelectUser | undefined, Error>({
    queryKey: ["/api/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: 2,
    staleTime: 5000, // 5 seconds
    enabled: authChecked, // Only run query after auth state is checked
    refetchOnMount: true,
  });

  // Add magic link verification function
  const verifyMagicLink = useCallback(
    async (token: string) => {
      try {
        console.log("Verifying magic link with token:", token);

        const response = await fetch("/api/verify-magic-link", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
          credentials: "include",
        });

        const data = await response.json();
        if (data.success) {
          console.log(
            "Magic link verification successful, user logged in:",
            data.user.id
          );

          // Store auth session marker
          localStorage.setItem("auth_session_active", "true");

          // Update the cache immediately
          queryClient.setQueryData(["/api/user"], data.user);

          // Invalidate and refetch to ensure fresh data
          queryClient.invalidateQueries({ queryKey: ["/api/user"] });

          try {
            await refetchUser();
            console.log("User data refetched successfully after magic link");
          } catch (err) {
            console.error("Error refetching user after magic link:", err);
          }

          toast({
            title: "Welcome back!",
            description: "You have been successfully logged in via magic link.",
          });

          // Force navigation to home/dashboard
          navigate("/onboarding");
        } else {
          console.error("Magic link verification failed:", data.message);
          toast({
            title: "Magic link expired",
            description:
              "This magic link has expired or is invalid. Please request a new one.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Magic link verification error:", error);
        toast({
          title: "Authentication failed",
          description: "Failed to verify magic link. Please try again.",
          variant: "destructive",
        });
      }
    },
    [refetchUser, toast]
  );

  // Add message listener for magic links
  // In your AuthProvider, change the message listener:
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      console.log("=== MESSAGE DEBUG ===");
      console.log("Message received:", event);
      console.log("Event data:", event.data);
      console.log("Event origin:", event.origin);
      console.log("Event source:", event.source);

      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        console.log("Parsed message data:", data);

        if (data.type === "magic-link-auth" && data.token) {
          console.log("Processing magic link with token:", data.token);
          await verifyMagicLink(data.token);
        }
      } catch (error) {
        console.error("Error parsing message:", error);
      }
    };

    // Try multiple event listeners
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);

    // Also try listening for React Native specific events
    const handleReactNativeMessage = (event) => {
      console.log("React Native message event:", event);
      handleMessage(event);
    };

    window.addEventListener("reactNativeMessage", handleReactNativeMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
      window.removeEventListener(
        "reactNativeMessage",
        handleReactNativeMessage
      );
    };
  }, [verifyMagicLink]);

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      console.log("Attempting login with username:", credentials.email);
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
        credentials: "include",
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Invalid username or password");
      }

      return await res.json();
    },
    onSuccess: async (user: SelectUser) => {
      console.log("Login successful, user data received:", user.id);

      // Store a login marker in localStorage to assist with persistent sessions
      localStorage.setItem("auth_session_active", "true");

      // Update the cache immediately
      queryClient.setQueryData(["/api/user"], user);

      // Explicitly invalidate and refetch to ensure we have fresh data
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });

      try {
        console.log("Explicitly refetching user data after login");
        await refetchUser();
        console.log("User data refetched successfully");
      } catch (err) {
        console.error("Error refetching user after login:", err);
      }

      toast({
        title: "Login successful",
        description: "Welcome!",
      });
    },
    onError: (error: Error) => {
      console.error("Login error:", error.message);
      // Clear any stale auth markers
      localStorage.removeItem("auth_session_active");

      toast({
        title: "Login failed",
        description: getUserMessage(error),
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (registrationData: RegistrationData) => {
      console.log("Attempting registration for:", registrationData.email);

      const registrationToken = sessionStorage.getItem("registrationToken");
      const res = await fetch("/api/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(registrationToken ? { Authorization: `Bearer ${registrationToken}` } : {}),
        },
        body: JSON.stringify(registrationData),
        credentials: "include",
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Registration failed");
      }

      return await res.json();
    },
    onSuccess: async (user: SelectUser) => {
      console.log("Registration successful, user created:", user.id);

      sessionStorage.removeItem("registrationToken");
      sessionStorage.removeItem("verifiedUser");

      // Store a login marker in localStorage
      localStorage.setItem("auth_session_active", "true");

      // Update cache
      queryClient.setQueryData(["/api/user"], user);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });

      // Make sure we have fresh data
      try {
        console.log("Refetching user data after registration");
        await refetchUser();
      } catch (err) {
        console.error("Error refetching user after registration:", err);
      }

      // toast({
      //   title: "Registration successful",
      //   description: "Your account has been created!",
      // });
    },
    onError: (error: Error) => {
      console.error("Registration error:", error.message);

      toast({
        title: "Registration failed",
        description: getUserMessage(error),
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      console.log("Attempting to logout user");
      const res = await fetch("/api/logout", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Logout failed");
      }
      return true;
    },
    onSuccess: () => {
      console.log("Logout successful, clearing local data");

      // Clear localStorage
      localStorage.removeItem("auth_session_active");
      localStorage.clear(); // Clear everything to be safe

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear all auth data from cache
      queryClient.setQueryData(["/api/user"], null);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });

      // Clear all queries to prevent stale data
      queryClient.clear();
      // Navigate to landing page (client-side)
      navigate("/landing-page");

      // Fallback hard reload to ensure session cleared server-side
      setTimeout(() => {
        window.location.href = "/landing-page";
      }, 100);
    },
    onError: (error: Error) => {
      console.error("Logout error:", error.message);
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      console.log("Attempting to delete user account");
      const res = await fetch("/api/delete-account", {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Account deletion failed");
      }
      return true;
    },
    onSuccess: () => {
      console.log("Account deletion successful, clearing local data");

      // Clear localStorage
      localStorage.removeItem("auth_session_active");
      localStorage.clear();

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear all auth data from cache
      queryClient.setQueryData(["/api/user"], null);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });

      // Clear all queries to prevent stale data
      queryClient.clear();

      // Redirect to landing page after deletion
      navigate("/landing-page");
      setTimeout(() => {
        window.location.href = "/landing-page";
      }, 100);
    },
    onError: (error: Error) => {
      console.error("Account deletion error:", error.message);
      toast({
        title: "Account deletion failed",
        description: getUserMessage(error),
        variant: "destructive",
      });
    },
  });

  const requestMagicLinkMutation = useMutation({
    mutationFn: async (email: string) => {
      console.log("Requesting magic link for:", email);

      const res = await fetch("/api/request-magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        credentials: "include",
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to send magic link");
      }

      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: "Magic link sent!",
        description: "Check your email and click the link to sign in.",
      });
    },
    onError: (error: Error) => {
      console.error("Magic link request error:", error.message);
      toast({
        title: "Failed to send magic link",
        description: getUserMessage(error),
        variant: "destructive",
      });
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
        deleteAccountMutation,
        requestMagicLinkMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
